// RoleGrantsGrid.tsx
import React, { useEffect, useMemo, useState } from 'react';
import axiosInstance from '../../main/initializers/axios';
import './RoleGrantsGrid.css';

import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';

import { ApiConstants } from '../../main/constants/ApiConstants';
import ResourceEditDialog from './ResourceEditDialog';
import AuthManager from '../../main/utils/authManager';
import JwtManager from '../../main/utils/jwtManager';
import { setUser } from '../../main/store/stores/user/user.store';
import { useDispatch } from 'react-redux';
import useGetUser from '../../main/hooks/useGetUser';
import { setPermission } from '../../main/store/stores/user/permission.store';
import { setMenu } from '../../main/store/stores/user/menu.store';
import { useTranslation } from 'react-i18next';

interface RoleGrant {
  roleId: number;
  resourceId: number;
  resourceTitle: string;
  resourceCode: string;
  resourceLabel: string;
  resourceUrl?: string;
  read: boolean;
  write: boolean;
  hasChanges?: boolean;
}

interface Props {
  roleId: number;
}

const normalizeGrantText = (value?: string) =>
  (value ?? '').trim().toLowerCase().replace(/[\s_-]+/g, '');

const RoleGrantsGrid: React.FC<Props> = ({ roleId }) => {
  const dispatch = useDispatch();
  const currentUser = useGetUser();
  const [grants, setGrants] = useState<RoleGrant[]>([]);
  const [original, setOriginal] = useState<RoleGrant[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [selectedResourceId, setSelectedResourceId] = useState<number | null>(null);

  const [query, setQuery] = useState('');

  const { i18n } = useTranslation();

  useEffect(() => {
    if (roleId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleId]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get<RoleGrant[]>(ApiConstants.Roles.grants, {
        params: { roleId },
      });

      const data = (res.data || []).map((x) => ({
        ...x,
        hasChanges: false,
      }));

      setGrants(data);
      setOriginal(data.map((x) => ({ ...x })));
    } finally {
      setLoading(false);
    }
  };

  const toggle = (resourceId: number, perm: 'read' | 'write') => {
    setGrants((prev) =>
      prev.map((g) => {
        if (g.resourceId !== resourceId) return g;

        const next = { ...g };

        if (perm === 'read') {
          next.read = !next.read;
          if (!next.read) next.write = false;
        } else {
          next.write = !next.write;
          if (next.write) next.read = true;
        }

        const o = original.find((x) => x.resourceId === resourceId);
        next.hasChanges = !!o && (o.read !== next.read || o.write !== next.write);

        return next;
      }),
    );
  };

  const hasChanges = useMemo(() => grants.some((g) => g.hasChanges), [grants]);
  const changesCount = useMemo(() => grants.filter((g) => g.hasChanges).length, [grants]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return grants;

    return grants.filter((g) => {
      const t = (g.resourceTitle ?? '').toLowerCase();
      const c = (g.resourceCode ?? '').toLowerCase();
      const l = (g.resourceLabel ?? '').toLowerCase();
      const u = (g.resourceUrl ?? '').toLowerCase();
      const id = g.resourceId.toString();
      return t.includes(q) || c.includes(q) || l.includes(q) || u.includes(q) || id.includes(q);
    });
  }, [grants, query]);

  const primaryGrants = useMemo(
    () =>
      filtered.filter(
        (g) => !!(g.resourceTitle ?? '').trim() || !!(g.resourceCode ?? '').trim() || !!(g.resourceLabel ?? '').trim(),
      ),
    [filtered],
  );

  const additionalGrants = useMemo(
    () =>
      filtered.filter(
        (g) => !(g.resourceTitle ?? '').trim() && !(g.resourceCode ?? '').trim() && !(g.resourceLabel ?? '').trim(),
      ),
    [filtered],
  );

  const save = async () => {
    if (!hasChanges) return;
    setSaving(true);
    try {
      await axiosInstance.post(ApiConstants.Roles.saveGrants, {
        roleId,
        grants: grants.map((g) => ({
          resourceId: g.resourceId,
          read: g.read,
          write: g.write,
        })),
      });
      const clean = grants.map((g) => ({ ...g, hasChanges: false }));
      setGrants(clean);
      setOriginal(clean.map((x) => ({ ...x })));

      if (currentUser.role.id == roleId) {
        const res = await AuthManager.getUserFromToken(JwtManager.accessToken);
        dispatch(setUser(res.user));
        dispatch(setPermission(res.permissions));
        dispatch(setMenu(res.menu));
      }
    } finally {
      setSaving(false);
    }
  };

  const renderGrantCard = (g: RoleGrant) => {
    const fallbackTitle = g.resourceTitle || g.resourceCode || g.resourceLabel || `Resource #${g.resourceId}`;
    const normalizedTitle = normalizeGrantText(g.resourceTitle || g.resourceLabel || g.resourceCode);
    const normalizedUrl = normalizeGrantText(g.resourceUrl);
    const shouldPromoteUrlToTitle = !!g.resourceUrl && !!normalizedUrl && normalizedUrl !== normalizedTitle;
    const title = shouldPromoteUrlToTitle ? g.resourceUrl! : fallbackTitle;
    const subtitle = shouldPromoteUrlToTitle ? fallbackTitle : g.resourceUrl || g.resourceLabel || '-';

    return (
      <div key={g.resourceId} className={`rgCard ${g.hasChanges ? 'isChanged' : ''}`}>
        <div className="rgCardTop">
          <div className="rgTitleBlock">
            <div className="rgTitle">{title}</div>
            <span className="rgCodePill">{g.resourceCode || `#${g.resourceId}`}</span>
          </div>

          <button
            className="ui-control ui-btn ui-btn--ghost rgEditBtn"
            type="button"
            title={i18n.t('roles.editResource')}
            onClick={() => {
              setSelectedResourceId(g.resourceId);
              setEditOpen(true);
            }}
          >
            <EditIcon fontSize="small" />
          </button>
        </div>

        <div className="rgUrlRow">
          <span className="rgUrlPill" title={subtitle}>
            {subtitle}
          </span>
        </div>

        <div className="rgPermRow">
          <label className="rgCheck">
            <input type="checkbox" checked={g.read} onChange={() => toggle(g.resourceId, 'read')} />
            <span>{i18n.t('roles.read')}</span>
          </label>

          <label className="rgCheck">
            <input type="checkbox" checked={g.write} disabled={!g.read} onChange={() => toggle(g.resourceId, 'write')} />
            <span>{i18n.t('roles.write')}</span>
          </label>

          {g.hasChanges && <span className="rgChangedTag">{i18n.t('roles.changed')}</span>}
        </div>
      </div>
    );
  };

  return (
    <div className="rgWrap">
      {/* Topbar (aligned with RolesDetailsGrid style) */}
      <div className="ui-panel rgTopbar">
        <div className="rgSearch">
          <SearchIcon className="rgSearchIcon" />
          <input className="ui-control ui-input" placeholder={i18n.t('generic.search')} value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>

        <div className="rgTopActions">
          <button className="ui-control ui-btn ui-btn--ghost" type="button" onClick={load} disabled={loading || saving}>
            <RefreshIcon fontSize="small" /> {i18n.t('kitchen.refresh')}
          </button>

          {/* <button className="ui-control ui-btn ui-btn--primary" type="button" onClick={save} disabled={!hasChanges || saving}>
            <SaveIcon fontSize="small" /> {i18n.t('generic.save')}
          </button> */}
        </div>
      </div>

      {/* Body */}
      <div className="bcCard rgBody">
        {loading ? (
          <div className="rgLoading">{i18n.t('roles.loading...')}</div>
        ) : filtered.length === 0 ? (
          <div className="rgEmpty">{i18n.t('roles.noResourcesFound...')}</div>
        ) : (
          <>
            {primaryGrants.length > 0 && <div className="rgList">{primaryGrants.map(renderGrantCard)}</div>}

            {additionalGrants.length > 0 && (
              <details style={{ marginTop: primaryGrants.length > 0 ? 16 : 0 }}>
                <summary style={{ cursor: 'pointer', fontWeight: 600, marginBottom: 12 }}>
                  Additional Roles ({additionalGrants.length})
                </summary>
                <div className="rgList">{additionalGrants.map(renderGrantCard)}</div>
              </details>
            )}
          </>
        )}
      </div>

      {/* Sticky save bar (only when changes exist) */}
      {hasChanges && (
        <div className="rgSticky">
          <div className="rgStickyInner">
            <div className="rgStickyText">
              {changesCount} {i18n.t('roles.change')}
              {changesCount === 1 ? '' : 's'} {i18n.t('roles.pending')}
            </div>

            <button className="ui-control ui-btn ui-btn--primary" type="button" onClick={save} disabled={saving}>
              <SaveIcon fontSize="small" /> {i18n.t('roles.saveChanges')}
            </button>
          </div>
        </div>
      )}

      <ResourceEditDialog isOpen={editOpen} resourceId={selectedResourceId} onClose={() => setEditOpen(false)} onSaved={load} />
    </div>
  );
};

export default RoleGrantsGrid;
